// PaymentsConfigView — admin global escolhe quais métodos de pagamento estão
// disponíveis na plataforma. Singleton — uma config para todo o sistema.
//
// Métodos:
//   - Pix (gerado pela aplicação, sem gateway externo)
//   - Cartão de crédito → escolhe gateway: mock (dev), Stripe, Mercado Pago
//
// "mockAlwaysSucceeds" controla se cobranças de teste sempre passam (true) ou
// simulam falha aleatória (false — útil pra exercitar fluxo de cartão reserva).

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  BookOpen,
  CreditCard,
  Loader2,
  QrCode,
  Save,
  Sparkles
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { Switch } from "@app/ui/components/switch";
import { api, ApiError, type PaymentsConfig } from "./api";
import { PROVIDER_DOCS, type ProviderId } from "./data/provider-docs";
import { ProviderDocsDrawer } from "./ProviderDocsDrawer";
import { friendlyError } from "@app/ui/lib/friendly-error";

const phase = signal<"loading" | "ready" | "error">("loading");
const cfg = signal<PaymentsConfig | null>(null);
const errMsg = signal<string | null>(null);
const saving = signal<boolean>(false);
const dirty = signal<boolean>(false);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.getPaymentsConfig();
    cfg.value = r.config;
    dirty.value = false;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

function patch(updates: Partial<PaymentsConfig>) {
  if (!cfg.value) return;
  cfg.value = { ...cfg.value, ...updates };
  dirty.value = true;
}

async function save() {
  if (!cfg.value || !dirty.value) return;
  saving.value = true;
  try {
    const r = await api.updatePaymentsConfig({
      pixEnabled: cfg.value.pixEnabled,
      platformPixKey: cfg.value.platformPixKey,
      platformPixRecipientName: cfg.value.platformPixRecipientName,
      platformPixCity: cfg.value.platformPixCity,
      platformPixMode: cfg.value.platformPixMode,
      pixProvider: cfg.value.pixProvider,
      ccEnabled: cfg.value.ccEnabled,
      ccProvider: cfg.value.ccProvider,
      ccConfig: cfg.value.ccConfig,
      mockAlwaysSucceeds: cfg.value.mockAlwaysSucceeds
    });
    cfg.value = r.config;
    dirty.value = false;
  } catch (e) {
    errMsg.value = friendlyError(e);
  } finally {
    saving.value = false;
  }
}

export function PaymentsConfigView() {
  const [docsOpen, setDocsOpen] = useState<ProviderId | null>(null);

  useEffect(() => {
    void load();
  }, []);

  if (phase.value === "loading") {
    return (
      <div class="space-y-2">
        <Skeleton class="h-32 w-full" />
        <Skeleton class="h-32 w-full" />
      </div>
    );
  }
  if (phase.value === "error" || !cfg.value) {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" />
        {errMsg.value ?? "Erro ao carregar."}
      </div>
    );
  }

  const c = cfg.value;
  const mockMode = c.ccProvider === "mock";

  return (
    <div class="space-y-4">
      <div class="rounded-md border border-blue-500/30 bg-blue-500/5 px-4 py-3 text-sm">
        <Sparkles className="mr-1 inline size-4 text-blue-600" />
        Estes ajustes controlam quais formas de pagamento aparecem para os
        alunos no checkout e em "Minha forma de pagamento" (assinaturas).
      </div>

      {/* Pix */}
      <section class="rounded-md border border-border bg-card p-6">
        <header class="mb-3 flex items-start gap-3">
          <div class="grid size-10 place-items-center rounded-md bg-emerald-500/15 text-emerald-600">
            <QrCode className="size-5" />
          </div>
          <div class="min-w-0 flex-1">
            <h3 class="text-sm font-bold">Pix</h3>
            <p class="text-xs text-muted-foreground">
              Gerado pela plataforma (EMV BR). Funciona pra compras avulsas e
              assinatura recorrente com brcode mensal.
            </p>
          </div>
          <Switch
            checked={c.pixEnabled}
            onCheckedChange={(v) => patch({ pixEnabled: v })}
          />
        </header>

        {c.pixEnabled && (
          <div class="space-y-3 border-t border-border pt-3">
            <div class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Recebedor Pix da plataforma
            </div>
            <p class="text-[11px] text-muted-foreground">
              Esses dados aparecem no QR/copia-e-cola das mensalidades da
              plataforma. Pra compras de pacote dentro de uma organização, o
              brcode usa o Pix da própria org (configurado em cada Company).
            </p>
            <div>
              <label class="mb-1 block text-xs font-medium text-muted-foreground">
                Chave Pix
              </label>
              <input
                type="text"
                value={c.platformPixKey}
                onInput={(e) =>
                  patch({
                    platformPixKey: (e.target as HTMLInputElement).value
                  })
                }
                placeholder="email, CPF, CNPJ, telefone ou chave aleatória"
                class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </div>
            <div class="grid gap-3 md:grid-cols-2">
              <div>
                <label class="mb-1 block text-xs font-medium text-muted-foreground">
                  Nome do recebedor (max 25 chars)
                </label>
                <input
                  type="text"
                  maxLength={25}
                  value={c.platformPixRecipientName}
                  onInput={(e) =>
                    patch({
                      platformPixRecipientName: (
                        e.target as HTMLInputElement
                      ).value
                    })
                  }
                  placeholder="EMPRESA LTDA"
                  class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm uppercase"
                />
              </div>
              <div>
                <label class="mb-1 block text-xs font-medium text-muted-foreground">
                  Cidade (max 15 chars)
                </label>
                <input
                  type="text"
                  maxLength={15}
                  value={c.platformPixCity}
                  onInput={(e) =>
                    patch({
                      platformPixCity: (e.target as HTMLInputElement).value
                    })
                  }
                  placeholder="SAO PAULO"
                  class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm uppercase"
                />
              </div>
            </div>
            <div class="space-y-2 rounded-md border border-dashed border-border bg-muted/30 px-3 py-2">
              <div class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Provedor do Pix
              </div>
              <div class="grid gap-2 sm:grid-cols-2">
                <ModeCard
                  active={c.pixProvider === "manual"}
                  title="Manual (gerado aqui)"
                  desc="Constrói o EMV/brcode localmente com a chave Pix acima. Sem reconciliação automática — admin confirma manualmente."
                  onClick={() => patch({ pixProvider: "manual" })}
                />
                <ModeCard
                  active={c.pixProvider === "celcoin"}
                  title="Celcoin (cel_cash)"
                  desc="Cada cobrança avulsa via API Celcoin (dinâmico, com valor + txid). Recorrência continua sendo nossa. Requer credenciais em Cartão→Celcoin abaixo."
                  onClick={() => patch({ pixProvider: "celcoin" })}
                  onShowDocs={() => setDocsOpen("celcoin")}
                />
              </div>
            </div>
            {c.pixProvider === "manual" && (
              <div class="space-y-2 rounded-md border border-dashed border-border bg-muted/30 px-3 py-2">
                <div class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Modo Pix manual
                </div>
                <div class="grid gap-2 sm:grid-cols-2">
                  <ModeCard
                    active={c.platformPixMode === "static"}
                    title="Estático (recomendado)"
                    desc="Aluno informa o valor no app do banco e se identifica pela mensagem. Igual ao fluxo de doação. Sem reconciliação automática."
                    onClick={() => patch({ platformPixMode: "static" })}
                  />
                  <ModeCard
                    active={c.platformPixMode === "dynamic"}
                    title="Dinâmico"
                    desc="QR já vem com valor + txid único por cobrança. Requer integração futura com PSP pra confirmação automática."
                    onClick={() => patch({ platformPixMode: "dynamic" })}
                  />
                </div>
              </div>
            )}
            {!c.platformPixKey && (
              <div class="rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
                <AlertCircle className="mr-1 inline size-3.5" />
                Sem chave Pix, mensalidades da plataforma em Pix vão falhar na
                geração do brcode. Configure antes de habilitar o plano Pro
                com Pix mensal.
              </div>
            )}
          </div>
        )}
      </section>

      {/* Cartão de crédito */}
      <section class="rounded-md border border-border bg-card p-6">
        <header class="mb-4 flex items-start gap-3">
          <div class="grid size-10 place-items-center rounded-md bg-violet-500/15 text-violet-600">
            <CreditCard className="size-5" />
          </div>
          <div class="min-w-0 flex-1">
            <h3 class="text-sm font-bold">Cartão de crédito</h3>
            <p class="text-xs text-muted-foreground">
              Permite assinatura recorrente automática. Alunos podem cadastrar
              um cartão primário e um reserva (entra automaticamente se o
              primeiro falhar).
            </p>
          </div>
          <Switch
            checked={c.ccEnabled}
            onCheckedChange={(v) => patch({ ccEnabled: v })}
          />
        </header>

        {c.ccEnabled && (
          <div class="space-y-3 border-t border-border pt-3">
            <div>
              <label class="mb-1 block text-xs font-medium text-muted-foreground">
                Gateway de pagamento
              </label>
              <div class="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                <GatewayCard
                  current={c.ccProvider}
                  value="mock"
                  title="Mock (teste)"
                  desc="Sem cobrança real. Use pra validar fluxo."
                  onClick={() => patch({ ccProvider: "mock" })}
                  onShowDocs={() => setDocsOpen("mock")}
                />
                <GatewayCard
                  current={c.ccProvider}
                  value="celcoin"
                  title="Celcoin (cel_cash)"
                  desc="Brasileiro. Pix avulso (recorrência nossa) + cartão capturado."
                  warning="Cole clientId/clientSecret abaixo."
                  onClick={() => patch({ ccProvider: "celcoin" })}
                  onShowDocs={() => setDocsOpen("celcoin")}
                />
                <GatewayCard
                  current={c.ccProvider}
                  value="stripe"
                  title="Stripe"
                  desc="Internacional, PaymentMethods + Subscriptions."
                  warning="Requer integração e credenciais."
                  onClick={() => patch({ ccProvider: "stripe" })}
                  onShowDocs={() => setDocsOpen("stripe")}
                />
                <GatewayCard
                  current={c.ccProvider}
                  value="mercadopago"
                  title="Mercado Pago"
                  desc="Brasileiro, suporta cartão + Pix integrados."
                  warning="Requer integração e credenciais."
                  onClick={() => patch({ ccProvider: "mercadopago" })}
                  onShowDocs={() => setDocsOpen("mercadopago")}
                />
              </div>

              {c.ccProvider === "celcoin" && (
                <CelcoinCredsBlock
                  config={c.ccConfig ?? {}}
                  onSave={(nextConfig) => patch({ ccConfig: nextConfig })}
                />
              )}
            </div>

            {mockMode && (
              <div class="rounded-md border border-dashed border-border bg-muted/30 px-3 py-2">
                <label class="flex items-center gap-3">
                  <Switch
                    checked={c.mockAlwaysSucceeds}
                    onCheckedChange={(v) => patch({ mockAlwaysSucceeds: v })}
                  />
                  <div class="min-w-0 flex-1">
                    <div class="text-sm font-medium">
                      Cobranças mock sempre aprovam
                    </div>
                    <div class="text-xs text-muted-foreground">
                      Quando desligado, ~30% das cobranças simulam falha — útil
                      pra testar fluxo de cartão reserva.
                    </div>
                  </div>
                </label>
              </div>
            )}

            {!mockMode && (
              <div class="rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
                <AlertCircle className="mr-1 inline size-3.5" />
                A integração do {c.ccProvider} ainda não está implementada.
                Cobranças vão falhar até finalizar a conexão com o gateway e
                configurar credenciais.
              </div>
            )}
          </div>
        )}
      </section>

      <ProviderDocsDrawer
        provider={docsOpen ? PROVIDER_DOCS[docsOpen] : null}
        onClose={() => setDocsOpen(null)}
      />

      <footer class="flex items-center justify-end gap-2">
        {dirty.value && (
          <span class="text-xs text-amber-600">Alterações não salvas</span>
        )}
        <button
          type="button"
          onClick={save}
          disabled={!dirty.value || saving.value}
          class="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:brightness-110 disabled:opacity-50"
        >
          {saving.value ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Save className="size-4" />
          )}
          Salvar
        </button>
      </footer>
    </div>
  );
}

function GatewayCard({
  current,
  value,
  title,
  desc,
  warning,
  onClick,
  onShowDocs
}: {
  current: string;
  value: "mock" | "stripe" | "mercadopago" | "celcoin";
  title: string;
  desc: string;
  warning?: string;
  onClick: () => void;
  onShowDocs?: () => void;
}) {
  const active = current === value;
  return (
    <div
      class={`relative rounded-md border p-3 transition-colors ${
        active
          ? "border-primary bg-primary/5 ring-1 ring-primary/40"
          : "border-border bg-background hover:border-primary/40"
      }`}
    >
      <button
        type="button"
        onClick={onClick}
        class="w-full text-left"
      >
        <div class="text-sm font-medium pr-6">{title}</div>
        <div class="mt-1 text-xs text-muted-foreground">{desc}</div>
        {warning && (
          <div class="mt-1 text-[11px] text-amber-600">{warning}</div>
        )}
      </button>
      {onShowDocs && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onShowDocs();
          }}
          class="mt-2 inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground hover:border-primary/40"
          title="Ver APIs externas e webhooks usados"
        >
          <BookOpen className="size-3" />
          Ver integração
        </button>
      )}
    </div>
  );
}

function ModeCard({
  active,
  title,
  desc,
  onClick,
  onShowDocs
}: {
  active: boolean;
  title: string;
  desc: string;
  onClick: () => void;
  onShowDocs?: () => void;
}) {
  return (
    <div
      class={`rounded-md border p-3 transition-colors ${
        active
          ? "border-primary bg-primary/5 ring-1 ring-primary/40"
          : "border-border bg-background hover:border-primary/40"
      }`}
    >
      <button type="button" onClick={onClick} class="w-full text-left">
        <div class="text-sm font-medium">{title}</div>
        <div class="mt-1 text-xs text-muted-foreground">{desc}</div>
      </button>
      {onShowDocs && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onShowDocs();
          }}
          class="mt-2 inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground hover:border-primary/40"
        >
          <BookOpen className="size-3" />
          Ver integração
        </button>
      )}
    </div>
  );
}

function CelcoinCredsBlock({
  config,
  onSave
}: {
  config: Record<string, unknown>;
  onSave: (next: Record<string, unknown>) => void;
}) {
  const initialId = String(config?.clientId ?? "");
  // O server agora NUNCA retorna o secret em claro — só `clientSecretSet`
  // boolean. Tratamos os dois formatos pra retrocompat com sessões abertas
  // antes do redeploy.
  const initialSecretMasked =
    config?.clientSecretSet === true || (typeof config?.clientSecret === "string" && config.clientSecret)
      ? "••••••••••••"
      : "";
  const [clientId, setClientId] = useState(initialId);
  const [newSecret, setNewSecret] = useState("");

  function persist() {
    // Não propaga campos derivados (*Set) — eles vêm do server pro front,
    // não do front pro server.
    const cleaned: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(config)) {
      if (!k.endsWith("Set")) cleaned[k] = v;
    }
    cleaned.clientId = clientId.trim();
    if (newSecret.trim()) cleaned.clientSecret = newSecret.trim();
    // Caso contrário, o server preserva o secret cifrado anterior.
    onSave(cleaned);
    setNewSecret("");
  }

  const dirty =
    clientId.trim() !== initialId || newSecret.trim().length > 0;

  return (
    <div class="space-y-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm">
      <header class="flex items-start gap-2">
        <div class="min-w-0 flex-1">
          <div class="font-semibold">Credenciais Celcoin (cel_cash)</div>
          <p class="mt-0.5 text-xs text-muted-foreground">
            Pegue em developers.celcoin.com.br → seu app → Credenciais.
            Mesmas creds servem pra Pix avulso e cartão. A chave secreta vai
            criptografada no DB.
          </p>
        </div>
      </header>
      <div class="grid gap-2 sm:grid-cols-2">
        <div>
          <label class="mb-1 block text-[11px] font-medium text-muted-foreground">
            Client ID
          </label>
          <input
            type="text"
            value={clientId}
            onInput={(e) => setClientId((e.target as HTMLInputElement).value)}
            placeholder="cel_xxxxxxxxxxxx"
            title="Client ID Celcoin"
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-xs"
          />
        </div>
        <div>
          <label class="mb-1 block text-[11px] font-medium text-muted-foreground">
            Client Secret {initialSecretMasked && <span class="text-emerald-600">({initialSecretMasked} salva)</span>}
          </label>
          <input
            type="password"
            value={newSecret}
            onInput={(e) => setNewSecret((e.target as HTMLInputElement).value)}
            placeholder={initialSecretMasked ? "Deixe vazio pra manter atual" : "Cole o secret"}
            title="Client Secret Celcoin"
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-xs"
          />
        </div>
      </div>
      <div class="flex items-center justify-between gap-2 text-[11px]">
        <span class="text-muted-foreground">
          Use o sandbox até trocar a URL base via env <code>CELCOIN_BASE_URL</code>.
        </span>
        <button
          type="button"
          onClick={persist}
          disabled={!dirty}
          class="rounded-md bg-primary px-3 py-1 text-[11px] font-medium text-primary-foreground disabled:opacity-50"
        >
          Salvar credenciais
        </button>
      </div>
    </div>
  );
}
